import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CardSkeleton } from '@/components/common/CardSkeleton';
import { useGetFixtureCountsQuery } from '@/store/api/fixtureApi';
import { useParlay } from '@/hooks/useParlay';

const sports = [
  {
    title: 'All',
    href: '/sports/all',
  },
  {
    title: 'Cricket',
    href: '/sports/cricket',
  },
  {
    title: 'Soccer',
    href: '/sports/soccer',
  },
  {
    title: 'Tennis',
    href: '/sports/tennis',
  },
];

interface SportsCardsProps {
  type: 'inplay' | 'today' | 'tomorrow';
}

// Whole-card artwork (title lettering + photo + the yellow arrow chip), cut
// from the design mockup with its baked-in count pill painted out — the count
// is drawn live on top. aspect = source width / height — kept matched to the
// source exactly (not narrowed down) because these images have content
// (title on the left, photo on the right) running edge to edge with no safe
// margin; any aspect other than the source's own crops into it with
// object-cover, e.g. the earlier bug that clipped Ronaldo's head. The
// column's width (not this ratio) is what changed to make the cards bigger.
const sportArt: Record<string, { image: string; aspect: number }> = {
  all: { image: '/images/sports-ui/bg-all.webp', aspect: 2.869 },
  cricket: { image: '/images/sports-ui/bg-cricket.webp', aspect: 3.232 },
  soccer: { image: '/images/sports-ui/bg-soccer.webp', aspect: 3.21 },
  tennis: { image: '/images/sports-ui/bg-tennis.webp', aspect: 2.961 },
};

/**
 * The live count, sitting where the mockup's pill was. Font size is measured
 * off the pill's own rendered height (ResizeObserver), not guessed from the
 * viewport — a vw-based guess fit the pill's width but not its height (the
 * pill's height comes from the card's height, which depends on the card's
 * own aspect ratio, not the viewport directly), so digits still ended up
 * taller than the box and got clipped top/bottom.
 */
function CountPill({ count }: { count: number | string }) {
  const pillRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState<number | null>(null);

  useEffect(() => {
    const el = pillRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const height = entry?.contentRect.height;
      if (!height) return;
      // Leaves clear margin above/below the digits — bold glyphs render
      // taller than their nominal font-size even at line-height: 1.
      setFontSize(height * 0.46);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <span
      ref={pillRef}
      className="absolute bottom-[8%] left-[3.5%] flex h-[36%] w-[24%] items-center justify-center overflow-hidden rounded-xl border-[1.5px] border-[#ffcf33] bg-gradient-to-b from-[#2e2e2e] to-black px-0.5 shadow-[0_2px_6px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.15)]"
    >
      <span
        className="font-black leading-none"
        style={{
          fontSize: fontSize ? `${fontSize}px` : 0,
          visibility: fontSize ? 'visible' : 'hidden',
          background: 'linear-gradient(180deg,#fff7b0 0%,#ffd21a 50%,#e59a00 100%)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        {count}
      </span>
    </span>
  );
}

export default function SportsCards({ type }: SportsCardsProps) {
  const { isParlayEnabled } = useParlay();
  const { data, isLoading } = useGetFixtureCountsQuery(
    { isParlay: isParlayEnabled },
    { pollingInterval: 5000 }
  );
  const counts = data?.counts[type];

  if (isLoading) {
    return <CardSkeleton length={4} />;
  }
  return (
    <div className="flex-1 space-y-2">
      {sports.map(({ title, href }) => {
        const art = sportArt[title.toLowerCase()];
        const count = (title === 'All' ? counts?.all : counts?.sports?.[title]) ?? 0;

        return (
          <Link
            key={`${type}-${title}`}
            to={`${href}/${type}`}
            aria-label={`${title === 'All' ? 'All sports' : title}: ${count}`}
            style={{ aspectRatio: art?.aspect }}
            className="relative block overflow-hidden rounded-2xl bg-white shadow-[0_3px_10px_rgba(160,120,0,0.25)] ring-1 ring-white/90"
          >
            {art && (
              <img
                src={art.image}
                alt=""
                draggable={false}
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}

            <CountPill count={count} />
          </Link>
        );
      })}
    </div>
  );
}
